import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, ArrowLeft, PenTool } from 'lucide-react';
import ContractsTable from './ContractsTable';
import ContractEdit from './ContractEdit';
import ContractDeleteModal from './ContractDeleteModal';
import ContractPreviewModal from './ContractPreviewModal';
import SignatureModal from './SignatureModal';
import '../../../../assets/css/ContractsModern.css';

// Action Bar Styles
const actionBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    background: 'var(--bg-secondary, #f5f7f9)',
    borderBottom: '1px solid var(--border-light, #e2e8f0)'
};

const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const signatureButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const ContractsIndex = () => {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // View state
    const [editingId, setEditingId] = useState(null);
    const [isNewContract, setIsNewContract] = useState(false);

    // Modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewContract, setPreviewContract] = useState(null);

    // Check URL params for edit
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const editId = params.get('edit');
        const isNew = params.get('new');

        if (editId) {
            setEditingId(parseInt(editId));
        }
        if (isNew === 'true') {
            setIsNewContract(true);
        }
    }, []);

    // Load contracts
    const fetchContracts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch('/index.php/contracts/getAllContractsJSON');
            if (!response.ok) {
                throw new Error('Fehler beim Laden der Verträge');
            }
            const data = await response.json();
            setContracts(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching contracts:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContracts();
    }, [fetchContracts]);

    // Update URL
    const updateUrl = (params) => {
        const url = new URL(window.location.href);
        Object.keys(params).forEach(key => {
            if (params[key] === null) {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, params[key]);
            }
        });
        window.history.pushState({}, '', url);
    };

    // Handle edit
    const handleEdit = (contract) => {
        setEditingId(contract.id);
        setIsNewContract(false);
        updateUrl({ edit: contract.id, new: null });
    };

    // Handle new
    const handleNew = () => {
        setIsNewContract(true);
        setEditingId(null);
        updateUrl({ new: 'true', edit: null });
    };

    // Handle back to list
    const handleBackToList = () => {
        setEditingId(null);
        setIsNewContract(false);
        updateUrl({ edit: null, new: null });
    };

    // Handle saved
    const handleSaved = () => {
        fetchContracts();
    };

    // Handle signature modal
    const handleSignature = () => {
        setSignatureModalOpen(true);
    };

    // Handle delete
    const handleDelete = (contract) => {
        setSelectedContract(contract);
        setDeleteModalOpen(true);
    };

    // Handle delete success
    const handleDeleteSuccess = () => {
        setDeleteModalOpen(false);
        setSelectedContract(null);
        fetchContracts();
    };

    // Handle duplicate
    const handleDuplicate = async (contract) => {
        try {
            const response = await fetch('/index.php/contracts/duplicateContractJSON', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: contract.id
                })
            });

            const result = await response.json();

            if (result.success) {
                fetchContracts();
            } else {
                setError(result.error || 'Fehler beim Duplizieren');
            }
        } catch (err) {
            console.error('Error duplicating contract:', err);
            setError('Fehler beim Duplizieren des Vertrags');
        }
    };

    // Handle preview
    const handlePreview = (contract) => {
        setPreviewContract(contract);
        setPreviewModalOpen(true);
    };

    // Styles
    const headerStyle = {
        marginBottom: '1.5rem'
    };

    const titleStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '1.5rem',
        fontWeight: '700',
        color: 'var(--text-primary, #2c3e50)',
        marginBottom: '0.5rem'
    };

    const descriptionStyle = {
        color: 'var(--text-secondary, #64748b)',
        fontSize: '0.875rem',
        marginBottom: 0
    };

    const newButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1.25rem',
        border: '1px solid var(--accent-slate, #5b7c8d)',
        borderRadius: '8px',
        background: 'transparent',
        color: 'var(--accent-slate, #5b7c8d)',
        fontSize: '0.875rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textDecoration: 'none'
    };

    // Show edit view
    if (editingId || isNewContract) {
        return (
            <div className="contracts-modern">
                <ContractEdit
                    contractId={editingId}
                    onBack={handleBackToList}
                    onSaved={handleSaved}
                    isNew={isNewContract}
                />
            </div>
        );
    }

    // Show list view
    return (
        <div className="contracts-modern">
            {/* Page Header */}
            <div style={headerStyle}>
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <h1 style={titleStyle}>
                            <FileText size={32} className="icon-contracts" />
                            Verträge
                        </h1>
                        <p style={descriptionStyle}>
                            Verwalten Sie Ihre Vertragsvorlagen für Jobs und Hochzeiten.
                        </p>
                    </div>
                    <button
                        style={newButtonStyle}
                        onClick={handleNew}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--accent-slate, #5b7c8d)';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--accent-slate, #5b7c8d)';
                        }}
                    >
                        <Plus size={20} />
                        Neuer Vertrag
                    </button>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}

            {/* Table Container */}
            <div className="table-container">
                {/* Action Bar */}
                <div style={actionBarStyle}>
                    <a href="index.php/settings" style={backButtonStyle}>
                        <ArrowLeft size={18} />
                        Zurück
                    </a>
                    <button
                        style={signatureButtonStyle}
                        onClick={handleSignature}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--bg-hover, #f1f5f9)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--bg-card, #ffffff)';
                        }}
                    >
                        <PenTool size={18} />
                        Unterschrift
                    </button>
                </div>
                <ContractsTable
                    data={contracts}
                    isLoading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    onPreview={handlePreview}
                />
            </div>

            {/* Delete Modal */}
            <ContractDeleteModal
                show={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setSelectedContract(null);
                }}
                onSuccess={handleDeleteSuccess}
                contractData={selectedContract}
            />

            {/* Signature Modal */}
            <SignatureModal
                show={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                onSaved={() => {
                    // Optional: show success message
                }}
            />

            {/* Preview Modal */}
            <ContractPreviewModal
                show={previewModalOpen}
                onClose={() => {
                    setPreviewModalOpen(false);
                    setPreviewContract(null);
                }}
                contractData={previewContract}
            />
        </div>
    );
};

export default ContractsIndex;
